import { Link as AmplifyLink, Flex } from '@aws-amplify/ui-react';
import { useContext, useState } from 'react';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { RxCornerBottomLeft } from 'react-icons/rx';
import { Link } from 'react-router-dom';
import { GlobalContext } from '../../global-context';
import { FieldName, SortOptions } from '../../utils/CustomTypes';
import General from '../../utils/GeneralUtils';
import { TableFields } from '../../utils/TableFields';
import ContactVerification from '../ContactVerification';
import EmailAddress from '../EmailAddress';
import PhoneNumber from '../PhoneNumber';
import NA from '../NA';
import SortLabel from '../SortLabel';
import TitleRankIndicator from '../TitleRankIndicator';
import UniversalTag from '../UniversalTag';
import { UrlAttribution } from '../UrlAttribution';
import './TableRowContact.scss';

export function TableRowHeaderContact({
  isAllSortingDisabled = false,
  showSubheader,
  currentSort,
  tableFields,
  onSort
}: {
  isAllSortingDisabled?: boolean,
  showSubheader: boolean,
  currentSort: SortOptions,
  tableFields: TableFields,
  onSort: (sortOptions: SortOptions) => void
}) {
  if (!showSubheader) return <></>
  return <>
    <div className='results-subheader' style={{ paddingLeft: 45 }}>
      <div style={{ flex: `1 1 500px` }}>
        { tableFields.isFieldVisible(FieldName.NAME) && <span style={{ marginRight: 10 }}><SortLabel fieldName={FieldName.NAME} isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.NAME)} currentSort={currentSort} onSort={onSort} /></span> }
        { tableFields.isFieldVisible(FieldName.TITLE) && <SortLabel fieldName={FieldName.TITLE} isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.TITLE)} currentSort={currentSort} onSort={onSort} /> }
      </div>
      {
        (
          tableFields.isFieldVisible(FieldName.EMAIL) ||
          tableFields.isFieldVisible(FieldName.PHONE)
        ) && <div style={{ flex: `1 1 385px` }}>
          { tableFields.isFieldVisible(FieldName.EMAIL) && <span style={{ marginRight: 10 }}><SortLabel fieldName={FieldName.EMAIL} isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.EMAIL)} currentSort={currentSort} onSort={onSort} /></span> }
          { tableFields.isFieldVisible(FieldName.PHONE) && <span style={{ marginRight: 10 }}><SortLabel fieldName={FieldName.PHONE} isDisabled={isAllSortingDisabled ? true : !tableFields.isFieldSortable(FieldName.PHONE)} currentSort={currentSort} onSort={onSort} /></span> }
        </div>
      }
      {/* <div style={{ flex: `1 1 40px` }}></div> */}
    </div>
  </>
}

function getContactInstitutions(row: any) {
  const firstDistrict = row?.belongsTo?.find((it: any) => it?.institution?.type === 'DISTRICT')
  const firstSchool = row?.belongsTo?.find((it: any) => it?.institution?.type === 'SCHOOL')
  const firstAgency = row?.belongsTo?.find((it: any) => it?.institution?.type === 'AGENCY')

  return {
    // Fall back to school if no district/agency (e.g., private school contacts)
    district: firstDistrict?.institution || firstAgency?.institution || firstSchool?.institution,
    school: firstSchool?.institution
  }
}

export function TableRowContact({
  row,
  tableFields,
  showRowActionButtons,
  showMainInstitution = true,
  onTagContact,
  onUntagContact,
  onEditContact,
  onSelectScreenshot,
  onRowClick,
}: {
  row: any,
  tableFields: TableFields,
  showRowActionButtons: boolean,
  showMainInstitution?: boolean,
  onTagContact: (data: any) => void,
  onUntagContact: (data: any) => void,
  onEditContact: (data: any) => void,
  onSelectScreenshot: (data: any) => void,
  onRowClick?: (e: React.MouseEvent) => void,
}) {
  const globalContext = useContext(GlobalContext);

  const [isFulltextExpanded, setIsFulltextExpanded] = useState(false)
  const [isDetailExpanded, setIsDetailExpanded] = useState(false)

  const allowedLabels = ['decision maker', 'educator']

  // if version.created_at is within the last 90 days, include it
  // const foundOnUrls = row?.foundOnUrls?.filter((it: any) => it?.versions?.some((version: any) => new Date().getTime() - new Date(version?.created_at).getTime() < 15552000000))
  const contactInstitutions = getContactInstitutions(row)
  const visibleAttributions = row?.attributions?.filter((it: any) => ['FOIA', 'URL_STAFF', 'URL_OTHER', 'EMAIL_DOMAIN'].includes(it?.attribution_type))?.sort((a: any, b: any) => new Date(b?.attribution_date).getTime() - new Date(a?.attribution_date).getTime())

  // Expose row click handler via callback
  const handleRowClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on a link or button
    const target = e.target as HTMLElement;
    if (target.tagName === 'A' || target.closest('a') || target.tagName === 'BUTTON' || target.closest('button') || target.closest('.tiny-button') || target.closest('.ignore-click')) {
      return;
    }
    setIsDetailExpanded(!isDetailExpanded);
    onRowClick?.(e);
  };

  return <>
    <div className='results-row-content' onClick={handleRowClick}>
      <div className='results-row-main-row' style={{ display: 'flex', flexWrap: 'nowrap' }}>
        <div style={{ flex: '1 1 500px', minWidth: 0 }}>
          <div className='results-row-main-line'>
            <Link className='main-table-link' to={`/directory/${globalContext.environment.entities.contact.urlSegment}/${row.id}`}>
              { General.trim(row.name, 50) || <NA /> }
            </Link>
            {
              row?.labels?.filter((it: any) => allowedLabels.includes(it))?.map((it: any) => {
                return <UniversalTag key={it} category={it} />
              })
            }
          </div>
          {
            tableFields.isFieldVisible(FieldName.TITLE) && row.title
              ? <Flex direction='row' columnGap={4} rowGap={1} className='main-table-title' wrap='wrap' alignItems='baseline' title={row.title}>
                  <div style={{ maxWidth: 580, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    { row.title }
                  </div>
                  {row.titleHistory && Array.isArray(row.titleHistory) && row.titleHistory.length > 1 && (
                    <span className="title-history-more">
                      +{row.titleHistory.length - 1} more
                    </span>
                  )}
                </Flex>
              : <></>
          }
          {/* {
            tableFields.isFieldVisible(FieldName.BIO) && row?.bios?.length > 0 &&
              <div className='desc-9'>{ row?.bios[0]?.bio }</div>
          } */}
          {
            showMainInstitution && <div className="contact-institution-row">
              {contactInstitutions?.district && (
                <Link className='silent' to={`/directory/institutions/${contactInstitutions.district.id || ''}`}>
                  {contactInstitutions.district.name}
                </Link>
              )}
            </div>
          }
          {
            row?.fulltextMatchedUrls?.length ?
              <div>
                <span
                  className='tiny-button'
                  onClick={() => {
                    setIsFulltextExpanded(!isFulltextExpanded)
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
                >
                  { row?.fulltextMatchedUrls?.length } URL{ row?.fulltextMatchedUrls?.length === 1 ? '' : 's' } matching fulltext search { isFulltextExpanded ? <BiChevronUp /> : <BiChevronDown /> }
                </span>
              </div> : <></>
          }
        </div>
        <div style={{ 
          flex: '1 1 323px',
          wordBreak: 'break-all',
          overflowWrap: 'break-word',
          minWidth: 0
        }}>
          {
            tableFields.isFieldVisible(FieldName.EMAIL) &&
              <div style={{ marginBottom: 5 }}>
                <EmailAddress 
                  email={row['email']}
                  emailDomainCount={row['emailDomainCount']}
                  emailVerificationStatus={row['emailVerificationStatus']}
                  emailLastVerifiedAt={row['emailLastVerifiedAt']}
                />
              </div>
          }
          {
            tableFields.isFieldVisible(FieldName.PHONE) &&
              <div>
                <PhoneNumber
                  phone={row['phone']}
                  phoneUniqueness={row['phoneUniqueness']}
                />
              </div>
          }
        </div>
        <div style={{ flex: `0 0 60px`, display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
          <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <ContactVerification verification={row?.verified} />
            {row?.titleRank !== undefined && row?.titleRank !== null && (
              <TitleRankIndicator
                titleRank={row.titleRank}
                titleModifier={row.titleModifier}
                titleArea={row.titleArea}
                titleRole={row.titleRole}
              />
            )}
          </div>
        </div>
      </div>
      <div>
        {
          isFulltextExpanded ?
            <div style={{ marginTop: 10, overflow: 'hidden' }}>
              {
                row?.fulltextMatchedUrls.map((match: { url: string; snippet: string }) => (
                  <div key={match.url} className='fulltext-url-item'>
                    <AmplifyLink
                      target='_blank'
                      href={match.url}
                    >
                      {General.ellipsisInMiddle(match.url, 110)}
                    </AmplifyLink>
                    {match.snippet && (
                      <div
                        className='fulltext-snippet'
                        dangerouslySetInnerHTML={{ __html: `...${match.snippet}...` }}
                      />
                    )}
                  </div>
                ))
              }
            </div> : <></>
        }
        {
          isDetailExpanded ?
            <div>
              {
                (() => {
                  // Combine title history and attributions into a single timeline
                  const timelineItems: any[] = [];
                  const seenTitleCombinations = new Map<string, any>();
                  const seenAttributionKeys = new Set<string>();

                  // Add title history items (only most recent for each title/attribution type combo)
                  if (row?.titleHistory && Array.isArray(row.titleHistory)) {
                    row.titleHistory.forEach((item: any) => {
                      if (item.attributions && Array.isArray(item.attributions)) {
                        item.attributions.forEach((attr: any) => {
                          const key = `${item.title}|${attr.type}`;
                          const date = new Date(attr.date);

                          // Track this attribution by date+type to avoid duplicates
                          const attrKey = `${attr.date}|${attr.type}`;

                          // Find the full attribution from visibleAttributions for URL info
                          let fullAttribution = null;
                          if (visibleAttributions && Array.isArray(visibleAttributions)) {
                            fullAttribution = visibleAttributions.find((a: any) =>
                              a.attribution_date === attr.date && a.attribution_type === attr.type
                            );
                          }

                          seenAttributionKeys.add(attrKey);

                          // Only keep the most recent occurrence of each title/attribution type combo
                          if (!seenTitleCombinations.has(key) || seenTitleCombinations.get(key).date < date) {
                            seenTitleCombinations.set(key, {
                              type: 'title',
                              date: date,
                              title: item.title,
                              attributionType: attr.type,
                              attribution: attr,
                              fullAttribution: fullAttribution
                            });
                          }
                        });
                      }
                    });
                  }

                  // Add the unique title combinations to timeline
                  seenTitleCombinations.forEach((item) => {
                    timelineItems.push(item);
                  });

                  // Add raw attributions (non-title related), but skip duplicates
                  if (visibleAttributions && Array.isArray(visibleAttributions)) {
                    visibleAttributions.forEach((attr: any) => {
                      const attrKey = `${attr.attribution_date}|${attr.attribution_type}`;

                      // Skip if we already have this attribution from titleHistory
                      if (!seenAttributionKeys.has(attrKey)) {
                        timelineItems.push({
                          type: 'attribution',
                          date: attr.attribution_date ? new Date(attr.attribution_date) : null,
                          attribution: attr
                        });
                      }
                    });
                  }

                  // Sort by date descending
                  timelineItems.sort((a, b) => b?.date?.getTime() - a?.date?.getTime());

                  if (timelineItems.length === 0) return null;

                  // Helper function to format time ago with months for > 30 days
                  const formatTimeAgo = (date: Date) => {
                    const now = new Date();
                    const diffMs = now.getTime() - date?.getTime();
                    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

                    if (diffDays < 30) {
                      return General.daysAgo(date.toISOString());
                    } else {
                      const diffMonths = Math.floor(diffDays / 30);
                      return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
                    }
                  };

                  return (
                    <div className="timeline-container">
                      {timelineItems.map((item, index) => {
                        const timeAgo = formatTimeAgo(item.date);

                        return (
                          <div key={index} className="timeline-item">
                            <div className="timeline-date">
                              <div className="timeline-line"></div>
                              <div className="timeline-dot"></div>
                              {
                                item?.date
                                  ? <>
                                      <div>{General.formatMonthYear(item.date)}</div>
                                      <div className='desc-3'>{timeAgo}</div>
                                    </>
                                  : <></>
                              }
                            </div>
                            <div className="timeline-content">
                            {item.type === 'title' ? (
                              <div className="table-row-contact__title-wrapper">
                                <div className="table-row-contact__title-line">
                                  <UniversalTag category='TITLE' /> <span className="timeline-title">{item.title}</span>
                                </div>
                                {item.attributionType === 'FOIA' ? (
                                  <div className="table-row-contact__attribution-wrapper">
                                    <RxCornerBottomLeft className="table-row-contact__attribution-icon" />
                                    <div className="table-row-contact__attribution-content">
                                      <UniversalTag category='VERIFIED' /> by the district
                                    </div>
                                  </div>
                                ) : item.attributionType === 'URL_STAFF' && item.fullAttribution?.urls?.urls?.url ? (
                                  <div className="table-row-contact__attribution-wrapper">
                                    <RxCornerBottomLeft className="table-row-contact__attribution-icon" />
                                    <div className="table-row-contact__attribution-content">
                                      Found on <UrlAttribution
                                        url={item.fullAttribution.urls.urls.url}
                                        status={item.fullAttribution.urls.urls.status}
                                        arnScreenshot={item.fullAttribution.urls.arn_screenshot}
                                        screenshotCreatedAt={item.fullAttribution.urls.created_at}
                                        prefix="staff page"
                                        maxUrlLength={76}
                                        onSelectScreenshot={onSelectScreenshot}
                                      />
                                    </div>
                                  </div>
                                ) : item.attributionType === 'URL_STAFF' ? (
                                  <div className="table-row-contact__attribution-wrapper">
                                    <RxCornerBottomLeft className="table-row-contact__attribution-icon" />
                                    <div className="table-row-contact__attribution-content">Found on staff page</div>
                                  </div>
                                ) : (
                                  <></>
                                )}
                              </div>
                            ) : (
                              <>
                                {item.attribution.attribution_type === 'FOIA' ? <span style={{ marginLeft: -7 }}><UniversalTag category='VERIFIED' /> Verified by the district</span> : <></>}
                                {item.attribution.attribution_type === 'EMAIL_DOMAIN' && `Email domain ${item.attribution?.domains?.id} matches a K-12 institution`}
                                {item.attribution.attribution_type === 'URL_STAFF' && (
                                  <span>
                                    Found on <UrlAttribution
                                      url={item.attribution?.urls?.urls?.url}
                                      status={item.attribution?.urls?.urls?.status}
                                      arnScreenshot={item.attribution?.urls?.arn_screenshot}
                                      screenshotCreatedAt={item.attribution?.urls?.created_at}
                                      prefix="staff page"
                                      maxUrlLength={60}
                                      onSelectScreenshot={onSelectScreenshot}
                                    />
                                  </span>
                                )}
                                {item.attribution.attribution_type === 'URL_OTHER' && (
                                  <span>
                                    Found outside <UrlAttribution
                                      url={item.attribution?.urls?.urls?.url}
                                      status={item.attribution?.urls?.urls?.status}
                                      arnScreenshot={item.attribution?.urls?.arn_screenshot}
                                      screenshotCreatedAt={item.attribution?.urls?.created_at}
                                      prefix="the staff page"
                                      maxUrlLength={76}
                                      showUrlOnSeparateLine={true}
                                      onSelectScreenshot={onSelectScreenshot}
                                    />
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  );
                })()
              }
              {/* {
                foundOnUrls.map((it: any) => (
                  <div key={it.url} style={{ fontSize: 14, marginTop: 5 }}>
                    <AmplifyLink
                      target='_blank'
                      href={ it?.url }
                    >
                      { General.ellipsisInMiddle(it?.url, 110) }{ it?.page_type?.some((type: any) => type?.startsWith('STAFF_')) ? <span style={{ marginLeft: 5 }} className='tag tag-blue'>Staff Page</span> : <></> }
                    </AmplifyLink>
                    {
                      Array.isArray(it?.versions) ?
                        <Flex gap={5} className="desc-2" alignItems='baseline'>
                          Scanned on
                          {
                            it.versions
                              .sort((a: any, b: any) => (new Date(b?.created_at) as any) - (new Date(a?.created_at) as any))
                              .slice(0, 3)
                              .map((version: any, index: number) => {
                                return <div key={version?.created_at}>
                                  {
                                    new Date(version.created_at).toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' })
                                  }
                                  {
                                    version?.screenshot ?
                                      <span className='clickable ignore-click' onClick={() => onSelectScreenshot(version)}> (screenshot)</span> :
                                      <></>
                                  }
                                  {
                                    index === it.versions.slice(0, 3).length - 1 ? '' : ','
                                  }
                                </div>
                              })
                          }
                        </Flex> :
                        <></>
                    }
                  </div>
                ))
              } */}
            </div> : <></>
        }
      </div>
    </div>
  </>
}
